import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatApiDate } from "../../utils/dateHelper";

export const getServiceTypeLabel = (servicesType) => {
  const raw = String(servicesType || "").trim();
  if (!raw) return "N/A";
  const normalized = raw.toLowerCase().replace(/[\s_-]/g, "");
  if (normalized === "overallvenue" || normalized === "overall") {
    return "ClubVenue";
  }
  if (normalized === "bottleservice") return "Bottle Service";
  if (normalized === "vipentery" || normalized === "vipentry") return "VIP Entry";
  return raw;
};

export const formatPayoutAmount = (value, currency = "cad") => {
  const num = Number(value);
  const amount = Number.isFinite(num) ? num.toFixed(2) : "0.00";
  return `${String(currency || "cad").toUpperCase()} ${amount}`;
};

export const formatPayoutDate = (dateStr) => formatApiDate(dateStr);

const toUtcDateOnly = (dateStr) => {
  if (!dateStr) return null;
  return moment.utc(dateStr).format("YYYY-MM-DD");
};

const apiDateToPickerDate = (dateStr) => {
  const day = toUtcDateOnly(dateStr);
  if (!day) return null;
  // Keep picker on the same calendar day as API (avoid timezone shift)
  return moment(day, "YYYY-MM-DD").toDate();
};

const pickerDateToDay = (date) => {
  if (!date) return null;
  return moment(date).format("YYYY-MM-DD");
};

export const getHostDisplayName = (row) => {
  const business =
    row?.hostBusinessName || row?.hostName || row?.host?.fullName || "";
  const club = String(row?.clubName || "").trim();
  const hasClub = club && club !== "—" && club.toLowerCase() !== "n/a";

  if (business && hasClub) return `${business} (${club})`;
  if (business) return business;
  if (hasClub) return club;
  return "N/A";
};

/**
 * Common transactions table for promoter payout detail.
 * Filters by start/end date (within payout fromDate–toDate) and service type.
 */
const PayoutTransactionsTable = ({
  transactions = [],
  fromDate = null,
  toDate = null,
  currency = "cad",
}) => {
  const payoutStart = apiDateToPickerDate(fromDate);
  const payoutEnd = apiDateToPickerDate(toDate);

  const [startDate, setStartDate] = useState(payoutStart);
  const [endDate, setEndDate] = useState(payoutEnd);
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setStartDate(apiDateToPickerDate(fromDate));
    setEndDate(apiDateToPickerDate(toDate));
    setServiceTypeFilter("");
    setCurrentPage(1);
  }, [fromDate, toDate, transactions]);

  const serviceTypeOptions = useMemo(() => {
    const map = new Map();
    (transactions || []).forEach((row) => {
      const key = row?.servicesType;
      if (!key) return;
      if (!map.has(key)) map.set(key, getServiceTypeLabel(key));
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [transactions]);

  const filteredRows = useMemo(() => {
    const startDay = pickerDateToDay(startDate);
    const endDay = pickerDateToDay(endDate);

    return (transactions || []).filter((row) => {
      if (serviceTypeFilter && row?.servicesType !== serviceTypeFilter) {
        return false;
      }

      const bookingDate = row?.bookingEndDate || row?.endDate || row?.startDate;
      if (!bookingDate) return true;

      const bookingDay = toUtcDateOnly(bookingDate);
      if (startDay && bookingDay < startDay) return false;
      if (endDay && bookingDay > endDay) return false;
      return true;
    });
  }, [transactions, serviceTypeFilter, startDate, endDate]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const getPaginationRange = () => {
    const maxVisiblePages = 5;
    const pages = [];
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }
    return pages;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <h6 className="mb-0 fw-semibold">Transactions</h6>
        <div className="d-flex align-items-center flex-wrap gap-3">
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
          </select>

          <div style={{ width: "160px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setCurrentPage(1);
              }}
              minDate={payoutStart || undefined}
              maxDate={endDate || payoutEnd || undefined}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="dd-MM-yyyy"
              isClearable
            />
          </div>
          <div style={{ width: "160px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              minDate={startDate || payoutStart || undefined}
              maxDate={payoutEnd || undefined}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="dd-MM-yyyy"
              isClearable
            />
          </div>

          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={serviceTypeFilter}
            onChange={(e) => {
              setServiceTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Service Type</option>
            {serviceTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>Service Type</th>
                <th>Amount</th>
                <th>Host Business Name</th>
                <th>Booking Date</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length > 0 ? (
                pageRows.map((row) => (
                  <tr key={row._id || row.paymentId}>
                    <td>{getServiceTypeLabel(row.servicesType)}</td>
                    <td>
                      {formatPayoutAmount(
                        row.amount,
                        row.currency || currency,
                      )}
                    </td>
                    <td>{getHostDisplayName(row)}</td>
                    <td>
                      {formatPayoutDate(
                        row.bookingStartDate ||
                          row.bookingDate ||
                          row.bookingEndDate ||
                          row.endDate ||
                          row.startDate,
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() =>
                  currentPage > 1 && setCurrentPage((prev) => prev - 1)
                }
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>
            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  type="button"
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                type="button"
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() =>
                  currentPage < totalPages &&
                  setCurrentPage((prev) => prev + 1)
                }
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PayoutTransactionsTable;
